import type React from 'react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { AlertCircle, CheckCircle2, RefreshCw, Sparkles } from 'lucide-react';

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Skeleton } from '@/components/ui/skeleton';
import {
  useProviderCheckoutContentSettings,
  useUpdateProviderCheckoutContentSettings,
} from '@/hooks/provider/useProviderCheckoutContent';
import {
  normalizeCheckoutBullets,
  normalizeCheckoutCopyField,
} from '@/services/provider/checkout-content.service';

type FormState = {
  headline: string;
  subheadline: string;
  bullet1: string;
  bullet2: string;
  bullet3: string;
};

type FormErrors = Partial<Record<keyof FormState, string>>;

const MAX_HEADLINE = 120;
const MAX_SUBHEAD = 200;
const MAX_BULLET = 160;

export function ProviderCheckoutContentCard() {
  const { t } = useTranslation();
  const settingsQuery = useProviderCheckoutContentSettings();
  const updateMutation = useUpdateProviderCheckoutContentSettings();

  const [formState, setFormState] = useState<FormState>({
    headline: '',
    subheadline: '',
    bullet1: '',
    bullet2: '',
    bullet3: '',
  });
  const [formErrors, setFormErrors] = useState<FormErrors>({});

  useEffect(() => {
    if (settingsQuery.data) {
      const bullets = settingsQuery.data.bullets ?? [];
      setFormState({
        headline: settingsQuery.data.headline ?? '',
        subheadline: settingsQuery.data.subheadline ?? '',
        bullet1: bullets[0] ?? '',
        bullet2: bullets[1] ?? '',
        bullet3: bullets[2] ?? '',
      });
    }
  }, [settingsQuery.data]);

  const isSaving = updateMutation.isPending;

  const placeholders = useMemo(
    () => ({
      headline: t('settings.checkoutContent.placeholders.headline', 'Launch faster with confidence'),
      subheadline: t(
        'settings.checkoutContent.placeholders.subheadline',
        'Everything you need to onboard and scale securely.',
      ),
      bullet1: t(
        'settings.checkoutContent.placeholders.bullet1',
        'Start building with a secure, fast API platform.',
      ),
      bullet2: t(
        'settings.checkoutContent.placeholders.bullet2',
        'Transparent pricing with predictable renewals.',
      ),
      bullet3: t(
        'settings.checkoutContent.placeholders.bullet3',
        'Priority support to help you launch faster.',
      ),
    }),
    [t],
  );

  const validate = (state: FormState): FormErrors => {
    const next: FormErrors = {};

    if (state.headline && state.headline.length > MAX_HEADLINE) {
      next.headline = t('settings.checkoutContent.validation.headlineTooLong');
    }

    if (state.subheadline && state.subheadline.length > MAX_SUBHEAD) {
      next.subheadline = t('settings.checkoutContent.validation.subheadlineTooLong');
    }

    (['bullet1', 'bullet2', 'bullet3'] as const).forEach((key) => {
      const value = state[key];
      if (value && value.length > MAX_BULLET) {
        next[key] = t('settings.checkoutContent.validation.bulletTooLong');
      }
    });

    return next;
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    const errors = validate(formState);
    setFormErrors(errors);
    if (Object.keys(errors).length > 0) return;

    const headline = normalizeCheckoutCopyField(formState.headline);
    const subheadline = normalizeCheckoutCopyField(formState.subheadline);
    const bullets = normalizeCheckoutBullets([
      formState.bullet1,
      formState.bullet2,
      formState.bullet3,
    ]);

    try {
      const updated = await updateMutation.mutateAsync({
        headline,
        subheadline,
        bullets,
      });
      setFormState({
        headline: updated.headline ?? '',
        subheadline: updated.subheadline ?? '',
        bullet1: updated.bullets[0] ?? '',
        bullet2: updated.bullets[1] ?? '',
        bullet3: updated.bullets[2] ?? '',
      });
      toast.success(t('settings.checkoutContent.saveSuccess'));
    } catch (error) {
      const message =
        error instanceof Error && error.message
          ? error.message
          : t('settings.checkoutContent.saveError');
      toast.error(message);
    }
  };

  if (settingsQuery.isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-muted-foreground" />
            {t('settings.checkoutContent.title', 'Checkout messaging')}
          </CardTitle>
          <CardDescription>
            {t(
              'settings.checkoutContent.description',
              'Customize the headline and bullets shown on your checkout page.',
            )}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-28" />
        </CardContent>
      </Card>
    );
  }

  if (settingsQuery.isError) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <AlertCircle className="h-5 w-5" />
            {t('settings.checkoutContent.title', 'Checkout messaging')}
          </CardTitle>
          <CardDescription>
            {t(
              'settings.checkoutContent.description',
              'Customize the headline and bullets shown on your checkout page.',
            )}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Alert variant="destructive">
            <AlertTitle>
              {t('settings.checkoutContent.loadError', 'Unable to load checkout messaging.')}
            </AlertTitle>
            <AlertDescription className="mt-2 flex items-center gap-3">
              <span>{settingsQuery.error.message}</span>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => settingsQuery.refetch()}
                className="gap-2"
              >
                <RefreshCw className="h-4 w-4" />
                {t('common.retry', 'Retry')}
              </Button>
            </AlertDescription>
          </Alert>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Sparkles className="h-5 w-5 text-muted-foreground" />
          {t('settings.checkoutContent.title', 'Checkout messaging')}
        </CardTitle>
        <CardDescription>
          {t(
            'settings.checkoutContent.description',
            'Customize the headline and bullets shown on your checkout page.',
          )}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form className="space-y-4" onSubmit={handleSubmit}>
          <div className="space-y-2">
            <label className="text-sm font-medium">
              {t('settings.checkoutContent.fields.headline', 'Headline')}
            </label>
            <Input
              value={formState.headline}
              placeholder={placeholders.headline}
              onChange={(event) => setFormState((prev) => ({ ...prev, headline: event.target.value }))}
            />
            <p className="text-xs text-muted-foreground">
              {t('settings.checkoutContent.helper.headline', 'Shown as the main title on checkout.')}
            </p>
            {formErrors.headline ? (
              <p className="text-sm text-destructive">{formErrors.headline}</p>
            ) : null}
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium">
            {t('settings.checkoutContent.fields.subheadline', 'Subheadline')}
            </label>
            <Input
              value={formState.subheadline}
              placeholder={placeholders.subheadline}
              onChange={(event) => setFormState((prev) => ({ ...prev, subheadline: event.target.value }))}
            />
            <p className="text-xs text-muted-foreground">
              {t('settings.checkoutContent.helper.subheadline', 'Brief supporting copy below the headline.')}
            </p>
            {formErrors.subheadline ? (
              <p className="text-sm text-destructive">{formErrors.subheadline}</p>
            ) : null}
          </div>

          <div className="grid gap-4 md:grid-cols-3">
            {(['bullet1', 'bullet2', 'bullet3'] as const).map((key, index) => (
              <div key={key} className="space-y-2">
                <label className="text-sm font-medium">
                  {t('settings.checkoutContent.fields.bullet', 'Bullet')} {index + 1}
                </label>
                <Input
                  value={formState[key]}
                  placeholder={placeholders[`bullet${index + 1}` as keyof typeof placeholders]}
                  onChange={(event) =>
                    setFormState((prev) => ({ ...prev, [key]: event.target.value }))
                  }
                />
                {formErrors[key] ? (
                  <p className="text-sm text-destructive">{formErrors[key]}</p>
                ) : null}
              </div>
            ))}
          </div>

          <div className="flex items-center justify-end gap-3">
            <Button
              type="button"
              variant="outline"
              onClick={() =>
                setFormState({ headline: '', subheadline: '', bullet1: '', bullet2: '', bullet3: '' })
              }
              disabled={isSaving}
            >
              {t('common.clear', 'Clear')}
            </Button>
            <Button type="submit" disabled={isSaving} className="gap-2">
              {isSaving && <CheckCircle2 className="h-4 w-4 animate-spin" />}
              {t('settings.checkoutContent.actions.save', 'Save messaging')}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}
